import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import {
  useWizardService,
  getCurrentStepId,
} from '@bingads-webui-react/wizard';
import { StepList as ResponsiveStepList } from '@bingads-webui-react/wizard-ui-library';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { getAllExpandStepIds, parseStepConfig, getDefaultItemAriaLabel } from '@bingads-webui-react/step-progress-indicator';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import classNames from 'classnames';
import { CheckmarkCircle20Regular, Circle20Filled, Circle20Regular } from '@fluentui/react-icons';
import { SDKWizardStepProgress } from './sdk-wizard-step-progress';

const getStyles = () => ({
  listContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: 16,
    borderBottom: '1px solid #E0E0E0',
  },
  listItem: {
    cursor: 'pointer',
  },
  listItemProgress: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    '&.sdk-step-list-icon-selected': {
      color: '#0078D4',
    },
    '&.sdk-step-list-icon-behind': {
      color: '#107C10',
    },
    '&.sdk-step-list-icon-default': {
      color: '#C8C6C4',
    },
  },
  title: {
    '&.sdk-wizard-step-title-selected': {
      fontWeight: 'bold',
    },
    '&.sdk-wizard-step-title-default': {
      color: '#A19F9D',
    },
  },
});

const getIcon = (step, styles) => {
  if (step.isSelected || step.containsSelected) {
    return <Circle20Filled className={classNames('sdk-step-list-icon-selected', 'sdk-step-list-icon', styles.icon)} />;
  } else if (step.isBehind) {
    return <CheckmarkCircle20Regular className={classNames('sdk-step-list-icon-behind', 'sdk-step-list-icon', styles.icon)} />;
  }

  return <Circle20Regular className={classNames('sdk-step-list-icon-default', 'sdk-step-list-icon', styles.icon)} />;
};

const getProgressStatus = (step) => {
  let status = 'default';
  if (step.isSelected || step.containsSelected) {
    status = 'selected';
  } else if (step.isBehind) {
    status = 'behind';
  }
  return status;
};

export const SDKWizardStepListComponent = ({ steps, getOnItemClickFn, getItemAriaLabel }) => {
  const styles = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);

  return (
    <div className={classNames('sdk-wizard-step-list', styles.listContainer)}>
      {_.map(steps, (step, i) => {
        if (!step.isEnabled || !step.title) {
          return null;
        }

        const nextStep = i < steps.length - 1 ? steps[i + 1] : null;
        const icon = getIcon(step, styles);
        const onItemClick = getOnItemClickFn ? getOnItemClickFn(step) : undefined;
        const onItemKeyDown = onItemClick ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onItemClick();
          }
        } : undefined;

        const itemAriaLabel = getItemAriaLabel ? getItemAriaLabel(step) : getDefaultItemAriaLabel(step, i18n);
        const a11yProps = onItemClick ? {
          role: 'button',
          tabIndex: 0,
          onClick: onItemClick,
          onKeyDown: onItemKeyDown,
          'aria-label': itemAriaLabel,
        } : {};

        return (
          <div
            className={classNames('sdk-wizard-step-list-item', `sdk-wizard-step-list-item-${getProgressStatus(step)}`, styles.listItem)}
            {...a11yProps}
            key={step.title}
          >
            <div className={classNames('sdk-wizard-step-progress', styles.listItemProgress)}>
              <div className={classNames('sdk-wizard-step-list-icon', styles.iconButton)}>
                {icon}
              </div>
              {i < steps.length - 1 && <SDKWizardStepProgress status={getProgressStatus(nextStep)} />}
            </div>
            <div className={classNames(`sdk-wizard-step-title-${getProgressStatus(step)}`, styles.title)} title={step.title}>
              {step.title}
            </div>
            {step.renderDescription && step.renderDescription(step)}
          </div>
        );
      })}
    </div>
  );
};

SDKWizardStepListComponent.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
  })).isRequired,
  getOnItemClickFn: PropTypes.func.isRequired,
  getItemAriaLabel: PropTypes.func.isRequired,
};

export const SDKWizardStepListInternal = (props) => {
  const {
    config, getOnItemClickFn, getItemAriaLabel,
  } = props;
  const { state } = useWizardService();
  const currentStepId = getCurrentStepId(state);
  const currentFlow = useMemo(() => {
    if (state.context.currentFlow) {
      return state.context.currentFlow;
    }
    return getAllExpandStepIds(config);
  }, [config, state.context.currentFlow]);
  const steps = useMemo(() => parseStepConfig(config, currentStepId, currentFlow).filter(step => step.isEnabled && step.title), [config, currentFlow, currentStepId]);

  return (
    <SDKWizardStepListComponent steps={steps} getOnItemClickFn={getOnItemClickFn} getItemAriaLabel={getItemAriaLabel} />
  );
};

SDKWizardStepListInternal.propTypes = {
  config: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
    })),
  })).isRequired,
  getOnItemClickFn: PropTypes.func.isRequired,
  getItemAriaLabel: PropTypes.func.isRequired,
};

export const SDKWizardStepList = props => (
  <ResponsiveStepList
    {...props}
    LargeScreenStepListComponent={SDKWizardStepListInternal}
  />
);
